// oxlint-disable no-explicit-any
import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { CatalogType, ContainerContents, StabilityRuleSource, StabilityStage } from '../database/schema.js';
import { queries } from '../queries.js';
import { Status, zql } from '../schema.js';
import { itemsDetail, statusItems, valueItem } from '../util/event-log-items.js';
import { findTradeNameName, nameOf } from './utils/log-names.js';
import { getMissingRequiredStabilityStages } from '../util/stability-expiry.js';
import { pickActorFromUserActions, pickTimestampFromUserActions, titleCase } from '../util/index.js';
import { assertCan } from '../utils.js';
import { addEventLog, DeleteResourceSchema, EventLogResources, UpdateResourceStatusSchema, } from './util.js';
import { AddStabilityRuleSchema, createStabilityRule, deleteStabilityRule, updateStabilityRule, UpdateStabilityRuleSchema, } from './utils/stability-rules.js';
import { resolvePrescribedNameForSubstance } from './utils/prescribed-name.js';
/**
 * A reconstitution solvent row for a trade name. `id` is the solution drug id
 * (same convention as TradeNameConsumable). All solvent rows participate in the
 * reconstitution and each carries its own volume; the reconstituted solution's
 * concentration and volume expansion are stored on the trade name itself.
 *
 * The form keeps an empty `{id: '', solventVolume: 0}` placeholder row while
 * reconstitution is disabled, so empty placeholders are accepted here and
 * rejected by `tradeNameRefinement` once reconstitution is required.
 */
export const TradeNameReconstitutionSolvent = z.object({
    /** Solvent (solution) id. */
    id: z.uuidv7({ error: 'validation:this_field_is_required' }).or(z.literal('')),
    solventVolume: z.number().gte(0),
});
export const ActiveSubstanceTradeNameSchemaFields = {
    type: z.literal(CatalogType.enum.ACTIVE_SUBSTANCE),
    additionalApprovalRequired: z.boolean().nonoptional(),
    containsAlcohol: z.boolean().nonoptional(),
    // Strength logic
    strengthValue: z.number().gte(0).nonoptional(),
    strengthUnitId: z.uuidv7().nonempty(),
    reconstitutionRequired: z.boolean().optional(),
    reconstitutionSolvents: z.array(TradeNameReconstitutionSolvent).max(2).optional(),
    reconstitutedSolutionConcentration: z.number().gte(0).optional(),
    // An untouched form select submits '' for this optional field.
    reconstitutedConcentrationUnitId: z.uuidv7().or(z.literal('')).optional(),
    reconstitutedSolutionVolumeExpansion: z.number().gte(0).optional(),
    // Additionals logic
    additionalsUsed: z.boolean().optional(),
    additionalsDetails: z.string().optional(),
    additionalsWeight: z.number().gte(0).optional(),
};
export const ActiveSubstanceTradeNameSchema = z.object(ActiveSubstanceTradeNameSchemaFields);
export const SupportiveMedicationTradeNameTradeNameSchemaFields = {
    type: z.literal(CatalogType.enum.SUPPORTIVE_MEDICATION),
    // Strength logic
    strengthValue: z.number().gte(0).nonoptional(),
    strengthUnitId: z.uuidv7().nonempty(),
};
export const SupportiveMedicationTradeNameTradeNameSchema = z.object(SupportiveMedicationTradeNameTradeNameSchemaFields);
export const SolutionTradeNameSchemaFields = {
    type: z.literal(CatalogType.enum.SOLUTION),
    // Solution specific
    containerContents: ContainerContents.nonoptional(),
    maxVolumeCapacity: z.number().gte(0).nonoptional(),
    concentrationValue: z.number().gte(0).nonoptional(),
    concentrationUnitId: z.uuidv7().nonoptional(),
};
export const SolutionTradeNameSchema = z.object(SolutionTradeNameSchemaFields);
const AddTradeNameStabilityRule = AddStabilityRuleSchema.extend({ id: z.uuidv7().optional() });
export const TradeNameConsumable = z.object({ id: z.uuidv7(), units: z.int() });
// Base fields shared by all catalog types
export const BaseTradeNameSchema = z.object({
    name: z.string().trim().nonempty(),
    atcCode: z.string().optional(),
    isDrySubstance: z.boolean().nonoptional(),
    labelName: z.string().optional(),
    prescribedName: z.string().nonempty(),
    nationalId: z.string().optional(),
    barcodeGtin: z.string().optional(),
    isTrial: z.boolean(),
    externalInformationLink: z.string().optional(),
    description: z.string().optional(),
    productionNotes: z.string().optional(),
    density: z.number().gte(0).optional(),
    medicationProfileId: z.uuidv7().nonempty(),
    manufacturerId: z.uuidv7().optional(),
    unitsPerPackage: z.int().gte(1),
    minimumStock: z.int().gte(0).optional(),
    containerTypeId: z.uuidv7().optional(),
    containerMaterialId: z.uuidv7().optional(),
    // only for liquids
    volumeValue: z.number().gte(0).optional(),
    overfilledBy: z.number().gte(0).optional(),
    actualVolume: z.number().gte(0).optional(),
    // =====
    minimumSaveableVolume: z.number().gte(0).lte(100).optional().nullable(),
    stabilityRules: z.array(AddTradeNameStabilityRule),
    consumables: z.array(TradeNameConsumable).optional(),
});
const isMissing = (value) => {
    if (value === undefined || value === null || value === '')
        return true;
    if (Array.isArray(value) && value.length === 0)
        return true;
    return false;
};
function findTradeNameById(tx, id) {
    return tx.run(zql.tradeNames.where('id', id).one());
}
export function tradeNameRefinement(data, ctx) {
    const isLiquid = data.type === CatalogType.enum.SOLUTION || data.isDrySubstance === false;
    if (isLiquid) {
        if (data.volumeValue === undefined) {
            ctx.addIssue({ code: 'custom', message: 'This field is required.', path: ['volumeValue'] });
        }
    }
    if (data.type === CatalogType.enum.ACTIVE_SUBSTANCE && data.reconstitutionRequired) {
        const requiredFields = [
            { key: 'reconstitutionSolvents', label: 'Reconstitution Solvent' },
            { key: 'reconstitutedSolutionConcentration' },
            { key: 'reconstitutedConcentrationUnitId' },
            { key: 'reconstitutedSolutionVolumeExpansion' },
        ];
        requiredFields.forEach(({ key }) => {
            const value = data[key];
            if (isMissing(value)) {
                ctx.addIssue({ code: 'custom', message: 'This field is required.', path: [key] });
            }
        });
        // Placeholder rows (`id: ''`) are allowed while reconstitution is disabled;
        // once it's enabled every row needs a solvent.
        data.reconstitutionSolvents?.forEach((solvent, index) => {
            if (!solvent.id) {
                ctx.addIssue({
                    code: 'custom',
                    message: 'validation:this_field_is_required',
                    path: ['reconstitutionSolvents', index, 'id'],
                });
            }
        });
    }
    // A row with a chosen solvent but no volume is incomplete regardless of the
    // reconstitution toggle.
    data.reconstitutionSolvents?.forEach((solvent, index) => {
        if (solvent.id && solvent.solventVolume <= 0) {
            ctx.addIssue({
                code: 'custom',
                message: 'This field is required.',
                path: ['reconstitutionSolvents', index, 'solventVolume'],
            });
        }
    });
}
export const TradeNameSchema = z
    .discriminatedUnion('type', [
    BaseTradeNameSchema.extend(ActiveSubstanceTradeNameSchemaFields),
    BaseTradeNameSchema.extend(SupportiveMedicationTradeNameTradeNameSchemaFields),
    BaseTradeNameSchema.extend(SolutionTradeNameSchemaFields),
])
    .superRefine(tradeNameRefinement);
export const AddTradeNameSchema = TradeNameSchema;
export const UpdateTradeNameSchema = TradeNameSchema.and(z.object({
    id: z.uuidv7().nonempty(),
}));
export const UpdateTradeNameNationalIdSchema = z.object({
    id: z.uuidv7(),
    nationalId: z.string(),
});
function mapDBFields(args, prescribedNameId) {
    const common = {
        name: titleCase(args.name),
        labelName: args.labelName,
        nationalId: args.nationalId,
        prescribedNameId: prescribedNameId,
        barcodeGtin: args.barcodeGtin,
        isTrial: args.isTrial,
        externalInformationLink: args.externalInformationLink,
        density: args.density,
        manufacturerId: args.manufacturerId,
        medicationProfileId: args.medicationProfileId,
        unitsPerPackage: args.unitsPerPackage,
        minimumStock: args.minimumStock,
        containerTypeId: args.containerTypeId,
        containerMaterialId: args.containerMaterialId,
        volumeValue: args.volumeValue,
        overfilledBy: args.overfilledBy,
        actualVolume: args.actualVolume,
        description: args.description,
        productionNotes: args.productionNotes,
        minimumSaveableVolume: args.minimumSaveableVolume,
    };
    const extraFields = (() => {
        switch (args.type) {
            case CatalogType.enum.ACTIVE_SUBSTANCE:
                return {
                    reconstitutionRequired: args.reconstitutionRequired,
                    reconstitutedSolutionConcentration: args.reconstitutedSolutionConcentration,
                    // '' from an untouched form select means "no unit selected".
                    reconstitutedConcentrationUnitId: args.reconstitutedConcentrationUnitId || null,
                    reconstitutedSolutionVolumeExpansion: args.reconstitutedSolutionVolumeExpansion,
                    strengthValue: args.strengthValue,
                    strengthUnitId: args.strengthUnitId,
                    additionalApprovalRequired: args.additionalApprovalRequired,
                    containsAlcohol: args.containsAlcohol,
                    additionalsUsed: args.additionalsUsed,
                    additionalsDetails: args.additionalsDetails,
                    additionalsWeight: args.additionalsWeight,
                };
            case CatalogType.enum.SOLUTION:
                return {
                    containerContents: args.containerContents,
                    maxVolumeCapacity: args.maxVolumeCapacity,
                    concentrationValue: args.concentrationValue,
                    concentrationUnitId: args.concentrationUnitId,
                    containsAlcohol: false,
                };
            case CatalogType.enum.SUPPORTIVE_MEDICATION:
                return {
                    strengthValue: args.strengthValue,
                    strengthUnitId: args.strengthUnitId,
                    containsAlcohol: false,
                };
            default:
                return { containsAlcohol: false };
        }
    })();
    return { ...common, ...extraFields };
}
// #region Prescribed Name
async function resolvePrescribedNameId(tx, prescribedName, medicationProfileId, userActions) {
    const medicationProfile = await tx.run(zql.medicationProfiles
        .where('id', '=', medicationProfileId)
        .where('deletedAt', 'IS', null)
        .related('drugCatalogItem')
        .one());
    if (!medicationProfile?.drugCatalogItemId) {
        throw new Error('Medication profile or drug catalog item not found');
    }
    const matches = await tx.run(zql.prescribedNames
        .where(({ cmp, or }) => {
        const isUuid = z.uuid().safeParse(prescribedName).success;
        return isUuid
            ? or(cmp('name', '=', prescribedName), cmp('id', '=', prescribedName))
            : cmp('name', '=', prescribedName);
    })
        .where('deletedAt', 'IS', null)
        .related('drugCatalogItem'));
    // only a prescription trade name of the trade name's own substance may be used (CY-368)
    const resolution = resolvePrescribedNameForSubstance(matches.map(match => ({
        id: match.id,
        name: match.name,
        drugCatalogItemId: match.drugCatalogItemId,
        drugName: match.drugCatalogItem?.name ?? null,
    })), { id: medicationProfile.drugCatalogItemId, name: medicationProfile.drugCatalogItem?.name ?? 'this substance' });
    if (resolution.kind === 'conflict')
        throw new Error(resolution.message);
    if (resolution.kind === 'existing')
        return resolution.id;
    const timestamp = pickTimestampFromUserActions(userActions);
    const actor = pickActorFromUserActions(userActions);
    const newPrescribedName = {
        id: uuidv7(),
        name: prescribedName,
        drugCatalogItemId: medicationProfile.drugCatalogItemId,
        createdAt: timestamp,
        createdBy: actor,
    };
    await tx.mutate.prescribedNames.insert(newPrescribedName);
    return newPrescribedName.id;
}
// #endregion
// #region Reconstitution solvents
async function createTradeNameReconstitutionSolvents(tx, payload, userActions) {
    const timestamp = pickTimestampFromUserActions(userActions);
    const actor = pickActorFromUserActions(userActions);
    const tradeName = await findTradeNameById(tx, payload.tradeNameId);
    const solventIds = payload.reconstitutionSolvents.map(solvent => solvent.id).filter(Boolean);
    const solventName = nameOf(tx.location === 'server' && solventIds.length
        ? await tx.run(zql.drugCatalogItems.where('id', 'IN', solventIds))
        : []);
    // Placeholder rows (`id: ''`) carry no solvent and must not be persisted.
    const relationsToInsert = payload.reconstitutionSolvents
        .filter(solvent => solvent.id)
        .flatMap(solvent => {
        const newTradeNameReconSolvent = {
            id: uuidv7(),
            tradeNameId: payload.tradeNameId,
            solutionDrugId: solvent.id,
            solventVolume: solvent.solventVolume,
            createdAt: timestamp,
            createdBy: actor,
        };
        const promises = [tx.mutate.tradeNameReconstitutionSolvents.insert(newTradeNameReconSolvent)];
        if (tx.location === 'server') {
            const patches = createPatch({}, newTradeNameReconSolvent);
            promises.push(addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.tradeNameReconstitutionSolvent,
                resourceId: newTradeNameReconSolvent.id,
                action: 'create',
                patches: patches,
                status: 'OK',
                domain: 'configuration',
                details: {
                    id: tradeName?.id,
                    name: tradeName?.name,
                    itemName: tradeName?.name,
                    ...itemsDetail(valueItem('solvent', solventName(solvent.id))),
                },
                performedBy: actor,
                performedAt: timestamp,
            }));
        }
        return promises;
    });
    await Promise.all(relationsToInsert);
}
async function deleteTradeNameReconstitutionSolvents(tx, payload, userActions) {
    const timestamp = pickTimestampFromUserActions(userActions);
    const actor = pickActorFromUserActions(userActions);
    const removed = tx.location === 'server' && payload.tradeNameReconstitutionSolventsIds.length
        ? await tx.run(zql.tradeNameReconstitutionSolvents
            .where('id', 'IN', payload.tradeNameReconstitutionSolventsIds)
            .related('tradeName')
            .related('solutionDrug'))
        : [];
    const relationsToMarkAsDeleted = payload.tradeNameReconstitutionSolventsIds.flatMap(id => {
        const relation = removed.find(row => row.id === id);
        const deletedTradeNameReconSolvent = {
            id: id,
            deletedAt: timestamp,
            deletedBy: actor,
        };
        const promises = [tx.mutate.tradeNameReconstitutionSolvents.update(deletedTradeNameReconSolvent)];
        if (tx.location === 'server') {
            const patches = createPatch({}, deletedTradeNameReconSolvent);
            promises.push(addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.tradeNameReconstitutionSolvent,
                resourceId: deletedTradeNameReconSolvent.id,
                action: 'delete',
                patches: patches,
                status: 'WARNING',
                domain: 'configuration',
                details: {
                    itemName: relation?.tradeName?.name,
                    ...itemsDetail(valueItem('solvent', relation?.solutionDrug?.name)),
                },
                performedBy: actor,
                performedAt: timestamp,
            }));
        }
        return promises;
    });
    await Promise.all(relationsToMarkAsDeleted);
}
/**
 * Diffs the trade name's existing solvent rows against the submitted ones,
 * keyed by solution drug id (the UI prevents the same solvent in two rows):
 * new solvents are inserted, removed ones are soft deleted and rows whose
 * volume changed are updated in place. All rows participate in the
 * reconstitution; the total volume is the sum of the row volumes.
 */
async function replaceTradeNameReconstitutionSolvents(tx, payload, userActions) {
    const prevSolvents = payload.prev.reconstitutionSolvents || [];
    // Placeholder rows (`id: ''`) carry no solvent and are ignored.
    const newSolvents = (payload.newReconstitutionSolvents || []).filter(solvent => solvent.id);
    const newSolventsByDrugId = new Map(newSolvents.map(solvent => [solvent.id, solvent]));
    const prevByDrugId = new Map(prevSolvents.map(solvent => [solvent.solutionDrugId, solvent]));
    const addedSolvents = newSolvents.filter(solvent => !prevByDrugId.has(solvent.id));
    const deletedSolvents = prevSolvents.filter(solvent => !newSolventsByDrugId.has(solvent.solutionDrugId));
    const timestamp = pickTimestampFromUserActions(userActions);
    const actor = pickActorFromUserActions(userActions);
    const tradeName = await findTradeNameById(tx, payload.prev.id);
    const prevSolventIds = prevSolvents.map(solvent => solvent.solutionDrugId);
    const solventName = nameOf(tx.location === 'server' && prevSolventIds.length
        ? await tx.run(zql.drugCatalogItems.where('id', 'IN', prevSolventIds))
        : []);
    const updatedSolvents = prevSolvents.flatMap(prevSolvent => {
        const next = newSolventsByDrugId.get(prevSolvent.solutionDrugId);
        if (!next)
            return [];
        const hasChanged = prevSolvent.solventVolume !== next.solventVolume;
        if (!hasChanged)
            return [];
        const updatedTradeNameReconSolvent = {
            id: prevSolvent.id,
            solventVolume: next.solventVolume,
            updatedAt: timestamp,
            updatedBy: actor,
        };
        const promises = [tx.mutate.tradeNameReconstitutionSolvents.update(updatedTradeNameReconSolvent)];
        if (tx.location === 'server') {
            const patches = createPatch(prevSolvent, updatedTradeNameReconSolvent);
            promises.push(addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.tradeNameReconstitutionSolvent,
                resourceId: updatedTradeNameReconSolvent.id,
                action: 'update',
                patches: patches,
                status: 'OK',
                domain: 'configuration',
                details: {
                    id: tradeName?.id,
                    name: tradeName?.name,
                    itemName: tradeName?.name,
                    ...itemsDetail(valueItem('solvent', solventName(prevSolvent.solutionDrugId))),
                },
                performedBy: actor,
                performedAt: timestamp,
            }));
        }
        return promises;
    });
    if (addedSolvents.length) {
        await createTradeNameReconstitutionSolvents(tx, { reconstitutionSolvents: addedSolvents, tradeNameId: payload.prev.id }, userActions);
    }
    if (deletedSolvents.length) {
        await deleteTradeNameReconstitutionSolvents(tx, { tradeNameReconstitutionSolventsIds: deletedSolvents.map(item => item.id) }, userActions);
    }
    await Promise.all(updatedSolvents);
}
// #endregion
// #region Consumables
async function createTradeNameConsumables(tx, payload, userActions) {
    const timestamp = pickTimestampFromUserActions(userActions);
    const actor = pickActorFromUserActions(userActions);
    const tradeName = await findTradeNameById(tx, payload.tradeNameId);
    const consumableIds = payload.consumables.map(consumable => consumable.id);
    const consumableName = nameOf(tx.location === 'server' && consumableIds.length
        ? await tx.run(zql.consumables.where('id', 'IN', consumableIds))
        : []);
    const relationsToInsert = payload.consumables.flatMap(consumable => {
        const newTradeNameConsumable = {
            id: uuidv7(),
            tradeNameId: payload.tradeNameId,
            consumableId: consumable.id,
            units: consumable.units,
            createdAt: timestamp,
            createdBy: actor,
        };
        const promises = [tx.mutate.tradeNameConsumables.insert(newTradeNameConsumable)];
        if (tx.location === 'server') {
            const patches = createPatch({}, newTradeNameConsumable);
            promises.push(addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.tradeNameConsumable,
                resourceId: newTradeNameConsumable.id,
                action: 'create',
                patches: patches,
                details: {
                    id: tradeName?.id,
                    name: tradeName?.name,
                    itemName: tradeName?.name,
                    ...itemsDetail(valueItem('consumable', consumableName(consumable.id))),
                },
                status: 'OK',
                domain: 'configuration',
                performedBy: actor,
                performedAt: timestamp,
            }));
        }
        return promises;
    });
    await Promise.all(relationsToInsert);
}
async function deleteTradeNameConsumables(tx, payload, userActions) {
    const timestamp = pickTimestampFromUserActions(userActions);
    const actor = pickActorFromUserActions(userActions);
    const removed = tx.location === 'server' && payload.tradeNameConsumablesIds.length
        ? await tx.run(zql.tradeNameConsumables
            .where('id', 'IN', payload.tradeNameConsumablesIds)
            .related('tradeName')
            .related('consumable'))
        : [];
    const relationsToMarkAsDeleted = payload.tradeNameConsumablesIds.flatMap(id => {
        const relation = removed.find(row => row.id === id);
        const deletedTradeNameConsumable = {
            id: id,
            deletedAt: timestamp,
            deletedBy: actor,
        };
        const promises = [tx.mutate.tradeNameConsumables.update(deletedTradeNameConsumable)];
        if (tx.location === 'server') {
            const patches = createPatch({}, deletedTradeNameConsumable);
            promises.push(addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.tradeNameConsumable,
                resourceId: deletedTradeNameConsumable.id,
                action: 'delete',
                patches: patches,
                status: 'WARNING',
                domain: 'configuration',
                details: {
                    itemName: relation?.tradeName?.name,
                    ...itemsDetail(valueItem('consumable', relation?.consumable?.name)),
                },
                performedBy: actor,
                performedAt: timestamp,
            }));
        }
        return promises;
    });
    await Promise.all(relationsToMarkAsDeleted);
}
async function replaceTradeNameConsumables(tx, payload, userActions) {
    const prevItems = payload.prev.consumables || [];
    const nextItems = payload.newConsumables || [];
    const nextKeys = new Set(nextItems.map(i => `${i.id}-${i.units}`));
    const prevKeys = new Set(prevItems.map(i => `${i.consumableId}-${i.units}`));
    const addedConsumables = nextItems.filter(next => !prevKeys.has(`${next.id}-${next.units}`));
    const deletedConsumables = prevItems.filter(prev => !nextKeys.has(`${prev.consumableId}-${prev.units}`));
    if (addedConsumables.length > 0) {
        await createTradeNameConsumables(tx, { consumables: addedConsumables, tradeNameId: payload.prev.id }, userActions);
    }
    if (deletedConsumables.length > 0) {
        await deleteTradeNameConsumables(tx, { tradeNameConsumablesIds: deletedConsumables.map(item => item.id) }, userActions);
    }
}
// #endregion
// #region Stability rules
async function createTradeNameStabilityRule(tx, args, userActions) {
    // the stability rule can be assigned from a medication profile's stability rule, so we want to avoid creating another stability rule
    // we just want to create a link between that stability rule and the trade name
    let stabilityRuleId = args.id;
    if (!stabilityRuleId) {
        const stabilityRule = await createStabilityRule(tx, { ...args, source: StabilityRuleSource.enum.TRADE_NAME }, userActions, tx.location === 'server' ? await findTradeNameName(tx, args.tradeNameId) : undefined);
        stabilityRuleId = stabilityRule.id;
    }
    const actor = pickActorFromUserActions(userActions);
    const timestamp = pickTimestampFromUserActions(userActions);
    const tradeName = await findTradeNameById(tx, args.tradeNameId);
    const newTradeNameStabilityRule = {
        id: uuidv7(),
        stabilityRuleId: stabilityRuleId,
        tradeNameId: args.tradeNameId,
        createdAt: timestamp,
        createdBy: actor,
    };
    const promises = [tx.mutate.tradeNameStabilityRules.insert(newTradeNameStabilityRule)];
    if (tx.location === 'server') {
        const patches = createPatch({}, newTradeNameStabilityRule);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.tradeNameStabilityRule,
            resourceId: newTradeNameStabilityRule.id,
            action: 'create',
            patches: patches,
            details: {
                id: tradeName?.id,
                name: tradeName?.name,
                itemName: tradeName?.name,
            },
            status: 'OK',
            domain: 'configuration',
            performedBy: actor,
            performedAt: timestamp,
        }));
    }
    await Promise.all(promises);
    return newTradeNameStabilityRule;
}
export async function updateTradeNameStabilityRule(tx, args, userActions) {
    if (!args.id)
        return;
    const prev = await tx.run(zql.tradeNameStabilityRules
        .where('stabilityRuleId', args.id)
        .where('tradeNameId', args.tradeNameId)
        .related('stabilityRule')
        .where('deletedAt', 'IS', null)
        .one());
    if (prev?.stabilityRule?.source === StabilityRuleSource.enum.TRADE_NAME) {
        await updateStabilityRule(tx, args, userActions, tx.location === 'server' ? await findTradeNameName(tx, args.tradeNameId) : undefined);
    }
    const actor = pickActorFromUserActions(userActions);
    const timestamp = pickTimestampFromUserActions(userActions);
    const tradeName = await findTradeNameById(tx, args.tradeNameId);
    const updatedTradeNameStabilityRule = {
        id: prev?.id || uuidv7(),
        stabilityRuleId: args.id,
        tradeNameId: args.tradeNameId,
        updatedAt: timestamp,
        updatedBy: actor,
    };
    const promises = [tx.mutate.tradeNameStabilityRules.upsert(updatedTradeNameStabilityRule)];
    if (tx.location === 'server') {
        const patches = createPatch(prev, updatedTradeNameStabilityRule);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.tradeNameStabilityRule,
            resourceId: updatedTradeNameStabilityRule.id,
            action: 'update',
            patches: patches,
            details: {
                id: tradeName?.id,
                name: tradeName?.name,
                itemName: tradeName?.name,
            },
            status: 'OK',
            domain: 'configuration',
            performedBy: actor,
            performedAt: timestamp,
        }));
    }
    await Promise.all(promises);
    return updatedTradeNameStabilityRule;
}
async function deleteTradeNameStabilityRule(tx, { tradeNameStabilityRule }, userActions) {
    const stabilityRule = await tx.run(zql.stabilityRules.where('id', tradeNameStabilityRule.stabilityRuleId).where('deletedAt', 'IS', null).one());
    if (stabilityRule?.source === StabilityRuleSource.enum.TRADE_NAME) {
        await deleteStabilityRule(tx, tradeNameStabilityRule.stabilityRuleId, userActions, tx.location === 'server' ? await findTradeNameName(tx, tradeNameStabilityRule.tradeNameId) : undefined);
    }
    const timestamp = pickTimestampFromUserActions(userActions);
    const actor = pickActorFromUserActions(userActions);
    const deletedTradeNameStabilityRule = {
        id: tradeNameStabilityRule.id,
        deletedAt: timestamp,
        deletedBy: actor,
    };
    const promises = [tx.mutate.tradeNameStabilityRules.update(deletedTradeNameStabilityRule)];
    if (tx.location === 'server') {
        const patches = createPatch(tradeNameStabilityRule, deletedTradeNameStabilityRule);
        promises.push(addEventLog(tx, {
            id: uuidv7(),
            resource: EventLogResources.tradeNameStabilityRule,
            resourceId: deletedTradeNameStabilityRule.id,
            action: 'delete',
            patches: patches,
            status: 'WARNING',
            domain: 'configuration',
            details: {
                itemName: tx.location === 'server' ? await findTradeNameName(tx, tradeNameStabilityRule.tradeNameId) : undefined,
            },
            performedBy: actor,
            performedAt: timestamp,
        }));
    }
    await Promise.all(promises);
    return deletedTradeNameStabilityRule;
}
export async function replaceTradeNameStabilityRules(tx, { tradeName, newStabilityRules }, userActions) {
    const prevTradeNameStabilityRules = tradeName.stabilityRules || [];
    const updatedStabilityRules = newStabilityRules?.filter(item => Boolean(item.id)) || []; // an updated resource is one which already has an ID
    const updatedStabilityRulesIds = new Set(updatedStabilityRules.map(item => item.id));
    const addedStabilityRules = newStabilityRules?.filter(item => !item.id) || []; // a new resource is one which doesn't have an ID
    const deletedStabilityRules = prevTradeNameStabilityRules?.filter(item => !updatedStabilityRulesIds.has(item.stabilityRuleId)) || [];
    const timestamp = pickTimestampFromUserActions(userActions);
    const actor = pickActorFromUserActions(userActions);
    if (addedStabilityRules.length) {
        await Promise.all(addedStabilityRules.map(rule => createTradeNameStabilityRule(tx, { ...rule, tradeNameId: tradeName.id }, { createdAt: timestamp, createdBy: actor })));
    }
    if (updatedStabilityRules.length) {
        await Promise.all(updatedStabilityRules.map(rule => updateTradeNameStabilityRule(tx, { ...rule, tradeNameId: tradeName.id }, { updatedAt: timestamp, updatedBy: actor })));
    }
    if (deletedStabilityRules.length) {
        await Promise.all(deletedStabilityRules.map(rule => deleteTradeNameStabilityRule(tx, { tradeNameStabilityRule: rule }, { deletedAt: timestamp, deletedBy: actor })));
    }
}
// #endregion
async function findById(tx, id, ctx) {
    return (await tx.run(queries.products.getTradeNameById.fn({ args: id, ctx })));
}
/**
 * An active-substance trade name must carry an Open (or reconstitution) and a Prepared
 * stability rule assigned to the trade name itself. Solution and supportive-medication
 * trade names are not checked.
 */
function assertRequiredStabilityRules(args) {
    if (args.type !== CatalogType.enum.ACTIVE_SUBSTANCE)
        return;
    const missing = getMissingRequiredStabilityStages({
        type: args.type,
        isDrySubstance: args.isDrySubstance,
        ownStages: args.stabilityRules.map(rule => rule.stabilityStage),
    });
    if (missing.length > 0) {
        const labels = missing.map(stage => (stage === StabilityStage.enum.AFTER_PREPARATION ? 'Prepared' : 'Open'));
        throw new Error(`An active-substance trade name requires an Open and a Prepared stability rule (missing: ${labels.join(', ')}).`);
    }
}
export const tradeNames = {
    add: defineMutator(AddTradeNameSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'catalogue.manage');
        assertRequiredStabilityRules(args);
        const now = Date.now();
        const userActions = {
            createdAt: now,
            createdBy: ctx.user.id,
        };
        const prescribedNameId = await resolvePrescribedNameId(tx, args.prescribedName, args.medicationProfileId, userActions);
        const newTradeName = {
            ...mapDBFields(args, prescribedNameId),
            ...userActions,
            status: Status.enum.active,
            id: uuidv7(),
        };
        await tx.mutate.tradeNames.insert(newTradeName);
        if (tx.location === 'server') {
            const patches = createPatch({}, newTradeName);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.tradeName,
                resourceId: newTradeName.id,
                action: 'create',
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                domain: 'configuration',
                details: {
                    id: newTradeName.id,
                    name: newTradeName.name,
                },
            }, ctx);
        }
        if (args.type === CatalogType.enum.ACTIVE_SUBSTANCE && args.reconstitutionSolvents) {
            await createTradeNameReconstitutionSolvents(tx, {
                reconstitutionSolvents: args.reconstitutionSolvents,
                tradeNameId: newTradeName.id,
            }, userActions);
        }
        if (args.consumables) {
            await createTradeNameConsumables(tx, {
                consumables: args.consumables,
                tradeNameId: newTradeName.id,
            }, userActions);
        }
        if (args.stabilityRules) {
            await Promise.all(args.stabilityRules.map(rule => createTradeNameStabilityRule(tx, { ...rule, tradeNameId: newTradeName.id }, userActions)));
        }
    }),
    update: defineMutator(UpdateTradeNameSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'catalogue.manage');
        assertRequiredStabilityRules(args);
        const prev = await findById(tx, args.id, ctx);
        if (!prev)
            throw new Error('Not Found');
        const now = Date.now();
        const userActions = {
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        const prescribedNameId = await resolvePrescribedNameId(tx, args.prescribedName, args.medicationProfileId, userActions);
        const updatedTradeName = {
            ...mapDBFields(args, prescribedNameId),
            ...userActions,
            id: args.id,
        };
        await tx.mutate.tradeNames.update(updatedTradeName);
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedTradeName);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.tradeName,
                resourceId: args.id,
                action: 'update',
                patches: patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                domain: 'configuration',
                details: {
                    id: updatedTradeName.id,
                    name: updatedTradeName.name,
                },
            }, ctx);
        }
        if (args.type === CatalogType.enum.ACTIVE_SUBSTANCE && args.reconstitutionSolvents) {
            await replaceTradeNameReconstitutionSolvents(tx, { prev, newReconstitutionSolvents: args.reconstitutionSolvents }, userActions);
        }
        if (args.consumables) {
            await replaceTradeNameConsumables(tx, { prev, newConsumables: args.consumables }, userActions);
        }
        if (args.stabilityRules) {
            await replaceTradeNameStabilityRules(tx, { tradeName: prev, newStabilityRules: args.stabilityRules }, userActions);
        }
    }),
    updateNationalId: defineMutator(UpdateTradeNameNationalIdSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'catalogue.manage');
        const prev = await findById(tx, args.id, ctx);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const updatedTradeName = {
            id: args.id,
            nationalId: args.nationalId,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.tradeNames.update(updatedTradeName);
        if (tx.location === 'server') {
            const patches = createPatch({ nationalId: prev.nationalId }, { nationalId: args.nationalId });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.tradeName,
                resourceId: args.id,
                action: 'update',
                patches: patches,
                performedBy: updatedTradeName.updatedBy,
                performedAt: updatedTradeName.updatedAt,
                status: 'OK',
                domain: 'configuration',
                details: {
                    id: updatedTradeName.id,
                    name: prev.name,
                },
            }, ctx);
        }
    }),
    updateStatus: defineMutator(UpdateResourceStatusSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'catalogue.manage');
        const prev = await findById(tx, args.id, ctx);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const updatedTradeName = {
            id: args.id,
            status: args.status,
            updatedAt: now,
            updatedBy: ctx.user.id,
        };
        await tx.mutate.tradeNames.update(updatedTradeName);
        if (tx.location === 'server') {
            const patches = createPatch({ status: prev.status }, { status: args.status });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.tradeName,
                resourceId: args.id,
                action: 'update',
                patches: patches,
                performedBy: updatedTradeName.updatedBy,
                performedAt: updatedTradeName.updatedAt,
                status: args.status === 'inactive' ? 'WARNING' : 'INFO',
                domain: 'configuration',
                details: {
                    id: updatedTradeName.id,
                    name: prev.name,
                    ...itemsDetail(statusItems(prev.status, args.status)),
                },
            }, ctx);
        }
    }),
    delete: defineMutator(DeleteResourceSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'catalogue.manage');
        const prev = await findById(tx, args.id, ctx);
        if (!prev)
            throw new Error('Not found');
        const now = Date.now();
        const userActions = { deletedAt: now, deletedBy: ctx.user.id };
        const updatedTradeName = {
            id: args.id,
            name: `${prev.name}_deleted_${now}`,
            ...userActions,
        };
        await tx.mutate.tradeNames.update(updatedTradeName);
        if (tx.location === 'server') {
            const patches = createPatch({ name: prev.name }, { name: updatedTradeName.name });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: EventLogResources.tradeName,
                resourceId: args.id,
                action: 'delete',
                patches: patches,
                status: 'WARNING',
                domain: 'configuration',
                performedBy: updatedTradeName.deletedBy,
                performedAt: updatedTradeName.deletedAt,
                details: {
                    id: updatedTradeName.id,
                    name: prev.name,
                },
            }, ctx);
        }
        if (prev.reconstitutionSolvents?.length) {
            await deleteTradeNameReconstitutionSolvents(tx, { tradeNameReconstitutionSolventsIds: prev.reconstitutionSolvents.map(item => item.id) }, userActions);
        }
        if (prev.consumables?.length) {
            await deleteTradeNameConsumables(tx, { tradeNameConsumablesIds: prev.consumables.map(item => item.id) }, userActions);
        }
        if (prev.stabilityRules?.length) {
            await Promise.all(prev.stabilityRules.map(rule => deleteTradeNameStabilityRule(tx, { tradeNameStabilityRule: rule }, userActions)));
        }
    }),
};
//# sourceMappingURL=trade-name.mutators.js.map